import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'ts-debounce';
import { useBlocklistService } from '../services/BlocklistService';
import { useBlocklistsService } from '../services/BlocklistsService';
import { BlocklistEntryFormData } from '../types';
import { useBlocklistEntryFormDialogController } from './BlocklistEntryFormDialogController';
import { useBlocklistValidateDialogController } from './BlocklistValidateDialogController';

export const useBlocklistViewController = () => {
  const {
    loading: blocklistLoading,
    filter: blocklistFilter,
    list: blocklistList,
    getBlocklist,
    changeBlocklistFilter,
    resetBlocklist,
  } = useBlocklistsService();
  const { deleteBlocklistItem } = useBlocklistService();

  const blocklistEntryFormDialogController = useBlocklistEntryFormDialogController();
  const validateDialogController = useBlocklistValidateDialogController();

  const initLoad = useCallback(async () => {
    try {
      await getBlocklist();
    } catch (e) {
      console.log(e);
    }
  }, [getBlocklist]);

  const reloadBlocklist = useCallback(async () => {
    try {
      await getBlocklist();
    } catch (e) {
      console.log(e);
    }
  }, [getBlocklist]);

  const reloadBlocklistDebounce = useMemo(() => debounce(reloadBlocklist, 500), [reloadBlocklist]);

  const changeBlocklistSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeBlocklistFilter({ query });
      await reloadBlocklistDebounce();
    },
    [changeBlocklistFilter, reloadBlocklistDebounce],
  );

  const changeBlocklistSorting = useCallback(
    async (s: GridSortModel) => {
      changeBlocklistFilter({ sort: `${s[0].field}_${s[0].sort}` });
      await reloadBlocklist();
    },
    [changeBlocklistFilter, reloadBlocklist],
  );

  const changeBlocklistPagination = useCallback(
    async (pagination: GridPaginationModel) => {
      changeBlocklistFilter({
        page: pagination.page,
        limit: pagination.pageSize,
        offset: pagination.page * pagination.pageSize,
      });
      await reloadBlocklist();
    },
    [changeBlocklistFilter, reloadBlocklist],
  );

  const removeBlocklistItem = useCallback(
    async (itemId: string) => {
      try {
        await deleteBlocklistItem(itemId);
        await reloadBlocklist();
      } catch (e) {
        console.log(e);
      }
    },
    [deleteBlocklistItem, reloadBlocklist],
  );

  const openCreateDialogWithSearchValue = useCallback(() => {
    blocklistEntryFormDialogController.open(undefined, blocklistFilter.query);
  }, [blocklistEntryFormDialogController, blocklistFilter.query]);

  useEffect(() => {
    initLoad();

    return () => {
      resetBlocklist();
    };
  }, []);

  return {
    isPageLoading: blocklistLoading.getList,
    blocklistFilter,
    blocklistList,
    changeBlocklistSearchFilter,
    changeBlocklistSorting,
    changeBlocklistPagination,
    removeBlocklistItem,
    blocklistEntryFormDialogController: {
      ...blocklistEntryFormDialogController,
      submit: (full: BlocklistEntryFormData, dirty: Partial<BlocklistEntryFormData>) =>
        blocklistEntryFormDialogController.submit(full, dirty, () => reloadBlocklist()),
    },
    openCreateDialogWithSearchValue,
    validateDialogController,
  };
};
